import type { Condition } from '../CreateAutoscaleRulesModal/useAutoscaleRulesBuilder';

export type JsonLogicRule = Record<string, unknown>;

/**
 * Build a JSON Logic condition from UI rule structure
 *
 * Note: For Volume tags with multiple conditions, this returns an array
 * that should be flattened into the top-level 'and' array
 */
export function buildJsonLogicRule(uiCondition: Condition): JsonLogicRule | JsonLogicRule[] | null {
  if (!uiCondition.type || !uiCondition.conditionOperators.length) {
    return null;
  }

  let validConditions = uiCondition.conditionOperators.filter(
    (conditionOperator) => conditionOperator.condition && conditionOperator.value.length > 0
  );

  if (validConditions.length === 0) {
    return null;
  }

  // For volume tags and instance tags: deduplicate conditions with same operator and values
  if (uiCondition.type.tag === 'volume' || uiCondition.type.tag === 'instance_tag') {
    const deduplicatedMap = new Map<string, (typeof validConditions)[0]>();

    validConditions.forEach((condition) => {
      const operator = condition.condition!.operator;
      const sortedValues = [...condition.value].sort().join('|');
      const key = `${operator}:${sortedValues}`;

      // Only keep the first occurrence of each operator+values combination
      if (!deduplicatedMap.has(key)) {
        deduplicatedMap.set(key, condition);
      }
    });

    validConditions = Array.from(deduplicatedMap.values());
  }

  const conditionLogics = validConditions.map((condition) => {
    const conditionOperator = condition.condition!.operator;
    const ruleTypeTagName = uiCondition.type!.tag;

    let varName: string;
    if (ruleTypeTagName === 'volume') {
      varName = 'tags';
    } else if (ruleTypeTagName === 'instance_tag') {
      varName = 'instance_tags';
    } else if (ruleTypeTagName === 'instance') {
      varName = 'instance_id';
    } else if (ruleTypeTagName === 'node') {
      varName = 'node_group_name';
    } else if (ruleTypeTagName === 'cluster') {
      varName = 'cluster_name';
    } else {
      varName = 'instance_id';
    }

    const values = condition.value.map((v) => String(v));

    // Handle Volume Tags and Instance Tags - they iterate over an array
    if (ruleTypeTagName === 'volume' || ruleTypeTagName === 'instance_tag') {
      // Exact match (include)
      if (conditionOperator === 'is') {
        return {
          some: [
            { var: varName },
            {
              in: [{ var: '' }, values],
            },
          ],
        };
      }
      // Exact match (exclude)
      else if (conditionOperator === 'is not') {
        return {
          none: [
            { var: varName },
            {
              in: [{ var: '' }, values],
            },
          ],
        };
      }
      // Contains match (include)
      else if (conditionOperator === 'contains') {
        return {
          some: [
            { var: varName },
            {
              or: values.map((val) => ({
                in: [val, { var: '' }],
              })),
            },
          ],
        };
      }
      // Contains match (exclude)
      else if (conditionOperator === "doesn't contain") {
        return {
          none: [
            { var: varName },
            {
              or: values.map((val) => ({
                in: [val, { var: '' }],
              })),
            },
          ],
        };
      }
    }

    // Handle other types (Instance, Cluster, Node group) - single value fields
    // Exact match (include)
    if (conditionOperator === 'is') {
      return {
        in: [{ var: varName }, values],
      };
    }
    // Exact match (exclude)
    else if (conditionOperator === 'is not') {
      return {
        '!': {
          in: [{ var: varName }, values],
        },
      };
    }
    // Contains match (include)
    else if (conditionOperator === 'contains') {
      return {
        some: [
          values,
          {
            in: [{ var: '' }, { var: varName }],
          },
        ],
      };
    }
    // Contains match (exclude)
    else if (conditionOperator === "doesn't contain") {
      return {
        none: [
          values,
          {
            in: [{ var: '' }, { var: varName }],
          },
        ],
      };
    }

    // Fallback to exact match
    return {
      in: [{ var: varName }, values],
    };
  });

  if (conditionLogics.length === 1) {
    return conditionLogics[0];
  }

  // Volume tags and instance tags: return array of conditions (each 'some'/'none' is separate)
  if (uiCondition.type!.tag === 'volume' || uiCondition.type!.tag === 'instance_tag') {
    return conditionLogics;
  }

  // Non-volume tags with multiple conditions: wrap in 'and'
  return {
    and: conditionLogics,
  };
}
